import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Check, Award, Scale, Shield, Building2 } from "lucide-react";
import SEO from "../components/SEO";
import { partners, vision, mission } from "../data/firm";

const ABOUT_HERO =
  "https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

export default function About() {
  return (
    <>
      <SEO
        title="About the Firm"
        description="About K & K Associates — a New Delhi based law firm of Advocates & Solicitors with a philosophy of advising clients on how not to litigate. World-class legal services at competitive rates from our Defence Colony office."
        canonical="https://kandkassociates.com/about"
        keywords={["about K and K Associates", "best law firm Defence Colony", "law firm New Delhi about", "advocates solicitors Delhi history"]}
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://kandkassociates.com/" },
            { "@type": "ListItem", position: 2, name: "About" },
          ],
        }}
      />

      {/* Hero */}
      <section className="relative pt-28 md:pt-40 pb-16 md:pb-24 bg-bone border-b border-rule overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="kicker mb-6">The Firm</p>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[1] sm:leading-[0.98] text-ink">
              A practice built on <span className="italic text-brand">restraint,</span><br/>
              not on volume.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="font-serif text-xl text-ink/75 leading-relaxed">
              K &amp; K Associates is a Delhi based law firm of Advocates and Solicitors. We act for individuals, founders, established corporations, financial institutions and overseas clients across India's full legal landscape — from a single contract review to multi-year constitutional litigation.
            </p>
          </div>
        </div>
      </section>

      <div className="border-b border-rule">
        <img src={ABOUT_HERO} alt="Marble texture" className="w-full h-[40vh] object-cover" />
      </div>

      {/* Philosophy */}
      <section className="py-16 md:py-32 bg-paper">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <p className="kicker text-center mb-8">Our Philosophy</p>
          <Quote className="w-10 h-10 text-brand mx-auto mb-8" strokeWidth={1} />
          <p className="font-display text-3xl md:text-4xl lg:text-5xl text-center leading-tight text-ink">
            "We have experience handling issues at the pre-litigation stage and settling them through mediation and conciliation. <span className="italic text-brand">Above all, we advise clients on how not to litigate.</span>"
          </p>
          <div className="fine-rule w-32 mx-auto mt-12" />
        </div>
      </section>

      {/* Editorial body */}
      <section className="pb-16 md:pb-32 bg-paper">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 font-serif text-lg md:text-xl text-ink/80 leading-[1.85] space-y-8">
          <p className="dropcap">
            The firm was founded on a contrarian premise: in a country with one of the world's most overburdened judicial systems, the highest service a lawyer can offer is often to keep their client out of court. That belief shapes how we structure transactions, draft contracts, manage disputes and counsel boards.
          </p>
          <p>
            Where litigation is unavoidable, our advocates appear before every tier of the Indian judiciary — from district courts in Tis Hazari and Saket, to the Delhi High Court, the National Company Law Tribunal, the Debts Recovery Tribunal, the consumer commissions and ultimately the Supreme Court of India. Our office at A-197, LGF, Defence Colony, New Delhi 110024 — within 3 km of Saket District Court and 12 km of the Delhi High Court — is engineered for the pace at which serious litigation requires lawyers to move.
          </p>
          <p>
            We deliberately remain a <em>full-service boutique</em>. Twenty-two practice areas, anchored by a senior team that has tried matters in every relevant forum. The result: a single firm capable of carrying a client from the first legal notice to the final order — without relay races between consultants, with the same partner present from kickoff to closure, and at predictable, milestone-linked fees.
          </p>
          <p>
            Our commitment is simple: <strong>world class services at competitive rates which would be lucrative for every organisation.</strong> We measure success not in billed hours, but in problems closed.
          </p>
          <p>
            We work in English, Hindi and Punjabi. Our client roster spans manufacturing, banking, technology, healthcare, hospitality, real estate, family offices and overseas businesses. Many of our retainer engagements are 5+ years old — we earn renewal by being useful, not by being indispensable.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-bone py-16 md:py-32 border-y border-rule" data-testid="about-vision-mission">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-5">Vision & Mission</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule mt-8">
            <div className="bg-paper p-10 md:p-12">
              <h3 className="font-display text-3xl text-brand mb-5">Our Vision</h3>
              <p className="font-serif text-xl text-ink/80 leading-relaxed">{vision}</p>
            </div>
            <div className="bg-paper p-10 md:p-12">
              <h3 className="font-display text-3xl text-brand mb-5">Our Mission</h3>
              <p className="font-serif text-xl text-ink/80 leading-relaxed">{mission}</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Partners */}
      <section className="py-16 md:py-32 bg-paper" data-testid="about-partners">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="kicker mb-5">The Partners</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
              Counsel led from <span className="italic text-brand">the front.</span>
            </h2>
          </div>
          <div className="space-y-px bg-rule border border-rule">
            {partners.map((p) => (
              <div key={p.name} className="bg-paper p-10 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                  <div className="aspect-[4/5] overflow-hidden bg-bone mb-6 border border-rule">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <p className="kicker text-brand mb-3">{p.role}</p>
                  <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight">{p.name}</h3>
                  <p className="font-serif text-lg text-ink/70 mt-3">{p.experience}</p>
                  <div className="fine-rule w-16 my-6" />
                  <ul className="space-y-2">
                    {p.education.map((e) => (
                      <li key={e} className="flex items-start gap-2 font-serif text-sm text-ink/75">
                        <Check className="w-4 h-4 mt-1 shrink-0 text-brand" strokeWidth={1.5} />{e}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-8 font-serif text-lg text-ink/80 leading-relaxed space-y-5">
                  {p.bio.map((para) => <p key={para.slice(0, 40)}>{para}</p>)}
                </div>
              </div>
            ))}
          </div>
          <Link to="/company-profile" className="btn-outline mt-12 inline-flex" data-testid="about-company-profile-link">
            Read the full company profile <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Operating Principles */}
      <section className="bg-bone py-16 md:py-32 border-y border-rule">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-5">Operating Principles</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink max-w-3xl">
            The four standards we hold ourselves to.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 mt-16 border-t border-l border-rule">
            {[
              { n: "01", h: "Strategic Restraint", b: "Litigation is one of many tools. We advise on the cheapest, fastest, most durable resolution — even when it costs us a longer engagement. Many of our most successful files have no court record, only a signed settlement deed." },
              { n: "02", h: "Documentary Discipline", b: "Cases are won on paper. Every matter has a digital evidence vault, a chronology, and a witness brief from week one. We do not improvise at trial — we rehearse." },
              { n: "03", h: "Predictable Pricing", b: "Milestone-linked or capped retainer fees. No surprises. Clients should always know what the next step costs and what outcome it is structured to deliver." },
              { n: "04", h: "Pan-India Reach", b: "A vetted local-counsel network ensures coverage across every Indian court — without sacrificing the quality of central oversight from Defence Colony." },
            ].map((v) => (
              <div key={v.n} className="border-r border-b border-rule p-10 md:p-12 bg-paper">
                <p className="kicker text-brand mb-6">{v.n}</p>
                <h3 className="font-display text-3xl text-ink mb-5">{v.h}</h3>
                <p className="font-serif text-lg text-ink/70 leading-relaxed">{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-16 md:py-32 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="kicker mb-5">What Sets Us Apart</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
              Six qualities that define <span className="italic text-brand">our work product.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule">
            {[
              { Icon: Scale, h: "Senior Partner Attention", b: "Every matter has a partner in charge — not a delegated associate. Our senior partners draft critical pleadings and appear at material hearings personally." },
              { Icon: Award, h: "Documentary Rigour", b: "We document everything: chronologies, witness briefs, exhibit indexes, deposition rehearsals. No surprise testimonies, no missing exhibits." },
              { Icon: Shield, h: "Confidentiality First", b: "Strict NDA framework, secured digital portals, controlled paper access. Family disputes, M&A pre-signing, sensitive investigations — handled with discretion." },
              { Icon: Building2, h: "Cross-Disciplinary Bench", b: "Litigation, transactions, corporate, IP, real estate and family law on one desk. No relay races. No interpretive gaps." },
              { Icon: Scale, h: "Forum Familiarity", b: "Delhi NCR district courts, the Delhi High Court, NCLT, NCDRC, DRT, MACT and Family Courts — daily appearance experience translates to faster, sharper procedural choices." },
              { Icon: Award, h: "Predictable Outcomes", b: "We over-communicate timelines, costs and exposure. Clients get monthly updates and quarterly reviews. No financial surprises mid-mandate." },
            ].map(({ Icon, h, b }) => (
              <div key={h} className="bg-paper p-10">
                <Icon className="w-7 h-7 text-brand mb-6" strokeWidth={1.25} />
                <h3 className="font-display text-2xl text-ink mb-4">{h}</h3>
                <p className="font-serif text-base text-ink/70 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors Served */}
      <section className="bg-bone py-16 md:py-24 border-y border-rule">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="kicker mb-5">Sectors</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink">
                Industries where we have <span className="italic text-brand">deep familiarity.</span>
              </h2>
              <p className="mt-6 font-serif text-lg text-ink/75 leading-relaxed">
                Sector fluency matters: the same Companies Act provision lands differently in fintech regulation than it does in pharma compliance. Our partners maintain dedicated sector tracks.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-rule border border-rule">
              {[
                "Banking & NBFCs", "Insurance", "Real Estate & Infrastructure", "Manufacturing",
                "IT & SaaS", "Healthcare & Pharma", "Hospitality", "Education",
                "Media & Entertainment", "Retail & E-commerce", "Family Offices", "Start-ups & VC",
              ].map((s) => (
                <div key={s} className="bg-paper p-6 hover:bg-bone transition-colors">
                  <p className="font-serif text-base text-ink/85">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Office */}
      <section className="py-16 md:py-32 bg-paper">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-5">Our Office</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink mb-10">
            Defence Colony — engineered for <span className="italic text-brand">court proximity.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-12">
            {[
              { h: "3 km", b: "Saket District Court Complex — daily appearance routes" },
              { h: "8 km", b: "Supreme Court of India at Tilak Marg" },
              { h: "12 km", b: "Delhi High Court at Sher Shah Road" },
            ].map((c) => (
              <div key={c.h} className="border-l-2 border-brand pl-6">
                <p className="font-display text-5xl text-brand">{c.h}</p>
                <p className="kicker text-ink/60 mt-3">{c.b}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 font-serif text-lg text-ink/70 leading-relaxed">
            A-197, Lower Ground Floor, Defence Colony places us within a half-hour of every court that matters in Delhi NCR — and within minutes of the Ministry of Corporate Affairs office, RBI Delhi office, and the major regulator headquarters. For our clients, that translates to faster turnarounds and better-informed advice.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-paper">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <p className="kicker mb-6">Begin a Conversation</p>
          <h2 className="font-display text-4xl md:text-5xl text-ink leading-tight">
            Find out what is actually <span className="italic text-brand">in your interest</span>.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
              Book a Consultation <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
            <Link to="/services" className="btn-outline" data-testid="about-cta-services">
              Practice Areas
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
